<script setup lang="ts">
import { ref, computed } from 'vue';
import { downloads } from '@integro/core';
import type { RouteLocationRaw } from 'vue-router';
import ItpIcon from '../icon/ItpIcon.vue';
import './styles.scss';

const props = withDefaults(
  defineProps<{
    text?: string;
    icon?: string;
    to?: RouteLocationRaw;
    href?: string;
    target?: string;
    dropdownItem?: boolean;
    useAuth?: boolean;
    forceDownload?: boolean;
    downloadFileName?: string | null;
    showSpinner?: boolean;
    spinnerType?: 'border' | 'grow';
  }>(),
  {
    target: '_self',
    dropdownItem: false,
    useAuth: false,
    forceDownload: false,
    downloadFileName: null,
    showSpinner: false,
    spinnerType: 'grow',
  },
);

const emit = defineEmits<{
  (e: 'click', event: UIEvent): void;
}>();

const spinning = ref(false);

const url = computed(() =>
  props.href ? downloads.normalizeLink(props.href) : '',
);

const isPlainItem = computed(
  () => !props.dropdownItem && !props.href && !props.to,
);

const componentName = computed(() => {
  if (props.dropdownItem) {
    return 'BDropdownItem';
  }
  if (isPlainItem.value) {
    return 'li';
  }
  return 'BNavItem';
});

const componentClass = computed(() => ({
  'itp-nav-item': true,
  'nav-item': isPlainItem.value,
}));

const componentBindings = computed(() => {
  const bindings: Record<string, unknown> = { target: props.target };
  if (props.to) {
    bindings.to = props.to;
  }
  if (url.value) {
    bindings.href = url.value;
  }
  return bindings;
});

const NAV_ITEM_INTERACTIVE_SELECTOR = [
  'input',
  'select',
  'textarea',
  'label',
  'button',
  'a',
  '[role="button"]',
  '[role="switch"]',
  '[role="checkbox"]',
  '[role="menuitem"]',
  '.form-check',
  '.form-check-input',
  '.form-check-label',
  '.form-control',
  '.form-select',
].join(',');

async function onClick(ev: UIEvent) {
  if (isPlainItem.value) {
    ev.stopPropagation();
  }

  if (props.forceDownload) {
    return onDownloadClick(ev);
  }

  if (props.useAuth) {
    ev.preventDefault();

    if (props.showSpinner) {
      spinning.value = true;
    }

    try {
      const result = await downloads.getFileBLOB(
        url.value,
        props.downloadFileName,
      );
      if (result) {
        downloads.showFileInNewTab(result.file, result.fileName);
      }
    } finally {
      spinning.value = false;
    }
    return;
  }

  const target = ev.target as HTMLElement | null;
  if (target && typeof target.closest === 'function') {
    const interactive = target.closest(NAV_ITEM_INTERACTIVE_SELECTOR);
    if (interactive && interactive !== ev.currentTarget) {
      return;
    }
  }

  if (!props.href || props.href === '#') {
    ev.preventDefault();
    emit('click', ev);
  }
}

async function onDownloadClick(ev: UIEvent) {
  if (props.useAuth) {
    ev.preventDefault();

    const result = await downloads.getFileBLOB(
      url.value,
      props.downloadFileName,
    );
    if (result) {
      downloads.downloadBlob(result.file, result.fileName);
    }
  }
}
</script>

<template>
  <component
    :is="componentName"
    v-bind="componentBindings"
    :class="componentClass"
    @click="onClick"
  >
    <span
      v-if="spinning"
      :class="[
        spinnerType === 'border'
          ? 'spinner-border spinner-border-sm'
          : 'spinner-grow spinner-grow-sm',
      ]"
      role="status"
    >
      <span class="visually-hidden">Loading...</span>
    </span>

    <span v-if="icon" class="nav-item-icon">
      <ItpIcon :icon="icon" />
    </span>

    <span class="nav-item-content">
      <slot>
        <span v-if="text">{{ text }}</span>
      </slot>
    </span>
  </component>
</template>
